/**
 * Pictures for "Make your money work", drawn in code like the other questionnaires' (no chart library).
 * Colours come from the --vs-* variables when set.
 */
import { shortMoney } from './stayorgo';
const INK = 'var(--vs-ink,#1b2b28)';
const MUTED = 'var(--vs-muted,#6b7a76)';
const LINE = 'var(--vs-line,#d6dfdc)';
const SAVE = 'var(--vs-go,#f37f20)';
const LOAN = 'var(--vs-stay,#2f8f9d)';
const RED = 'var(--vs-lowest,#e06b4f)';
const esc = s => String(s).replace(/[&<>"]/g, c => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;'
})[c]);
export const HOME_LABELS = {
  abroad: 'Held abroad',
  tbills: 'Treasury bills and bonds',
  shares: 'Nigerian shares',
  land: 'Land or rentals',
  farming: 'Farming or commodities',
  business: 'A business'
};
const HOME_COLOURS = {
  abroad: 'var(--vs-go,#f37f20)',
  tbills: 'var(--vs-middle,#2f8f9d)',
  shares: 'var(--vs-upper,#3f9b6b)',
  land: 'var(--vs-property,#7a8f3a)',
  farming: 'var(--vs-lower,#e0a43a)',
  business: 'var(--vs-highest,#1f6f4a)'
};
function ticks(max, n) {
  const raw = max / n || 1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= raw);
  const out = [0];
  while (out[out.length - 1] < max - step * 0.001) out.push(out[out.length - 1] + step);
  return out;
}

/**
 * Your savings year by year against your number. line, lineGood, lineBad: savings from now; lineLoan: with borrowing.
 * date: the year savings reach the number (or null); age: your age now (to label years with ages).
 */
export function timeline(_ref) {
  let {
    line,
    lineGood,
    lineBad,
    lineLoan,
    number,
    currency,
    date,
    age
  } = _ref;
  const W = 340,
    H = 230,
    L = 54,
    R = 16,
    T = 22,
    B = 30;
  const years = line.length - 1;
  const top = Math.max(number * 1.15, ...line.slice(0, years + 1).map(v => Math.min(v, number * 1.6)));
  const ys = ticks(top, 4);
  const hi = ys[ys.length - 1];
  const X = t => L + (W - L - R) * t / years;
  const Y = v => T + (H - T - B) * (1 - Math.min(v, hi) / hi);
  const path = vals => vals.map((v, t) => (t ? 'L' : 'M') + X(t).toFixed(1) + ' ' + Y(v).toFixed(1)).join('');
  const label = v => shortMoney(v, currency);
  let svg = `<svg class="VMoney-chart" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc('Your number is ' + label(number) + '. ' + (date !== null && date !== undefined ? 'Your savings reach it in year ' + date + (age ? ', at about ' + (age + date) : '') + '.' : 'Your savings do not reach it in ' + years + ' years.'))}">`;
  ys.forEach(v => {
    svg += `<path d="M${L} ${Y(v).toFixed(1)}H${W - R}" stroke="${LINE}" stroke-width="1"/>`;
    svg += `<text x="${L - 6}" y="${(Y(v) + 3.5).toFixed(1)}" text-anchor="end" font-size="10" fill="${MUTED}">${esc(label(v))}</text>`;
  });
  const step = years > 40 ? 10 : years > 20 ? 5 : years > 10 ? 2 : 1;
  for (let t = 0; t <= years; t += step) {
    const txt = t === 0 ? 'Now' : age ? 'Age ' + (age + t) : 'Yr ' + t;
    svg += `<text x="${X(t).toFixed(1)}" y="${H - 10}" text-anchor="middle" font-size="10" fill="${MUTED}">${txt}</text>`;
  }
  // good and bad runs
  if (lineGood && lineBad) {
    svg += `<path d="${path(lineGood)}${lineBad.map((v, t) => 'L' + X(t).toFixed(1) + ' ' + Y(v).toFixed(1)).reverse().join('')}Z" fill="${SAVE}" fill-opacity=".15"/>`;
  }
  // your number
  svg += `<path d="M${L} ${Y(number).toFixed(1)}H${W - R}" stroke="${INK}" stroke-width="1.5" stroke-dasharray="5 4"/>`;
  svg += `<text x="${L + 4}" y="${(Y(number) - 6).toFixed(1)}" font-size="10.5" font-weight="700" fill="${INK}">Your number ${esc(label(number))}</text>`;
  if (lineLoan) svg += `<path d="${path(lineLoan)}" fill="none" stroke="${LOAN}" stroke-width="2.5" stroke-dasharray="6 4" stroke-linecap="round"/>`;
  svg += `<path d="${path(line)}" fill="none" stroke="${SAVE}" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>`;
  if (date !== null && date !== undefined && date <= years) {
    const cx = X(date),
      cy = Y(number);
    svg += `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="5.5" fill="#fff" stroke="${SAVE}" stroke-width="3"/>`;
    const txt = age ? 'Age ' + (age + date) : 'Year ' + date;
    const anchor = cx > W - 70 ? 'end' : 'middle';
    svg += `<text x="${cx.toFixed(1)}" y="${(cy + 20).toFixed(1)}" text-anchor="${anchor}" font-size="11" font-weight="700" fill="${SAVE}">${txt}</text>`;
  }
  return svg + '</svg>';
}

/** The loan beside what it bought: normally, and after a fall of fallShare */
export function stressBars(_ref2) {
  let {
    stress,
    currency
  } = _ref2;
  const W = 340,
    rowH = 38,
    L = 132,
    R = 70;
  const rows = [['You owe', stress.owed, RED], ['Worth in a normal year', stress.normal, SAVE], ['Worth if ' + stress.what + ' falls ' + Math.round(stress.fallShare * 100) + '%', stress.fall, 'var(--vs-lower,#e0a43a)']];
  const max = Math.max(...rows.map(r => r[1]));
  const H = rows.length * rowH + 4;
  let svg = `<svg class="VMoney-bars" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(rows.map(r => r[0] + ' ' + shortMoney(r[1], currency)).join('. '))}">`;
  rows.forEach((_ref3, i) => {
    let [t, v, c] = _ref3;
    const y = i * rowH + 6;
    const w = Math.max(2, (W - L - R) * v / max);
    svg += `<text x="${L - 8}" y="${y + 15}" text-anchor="end" font-size="10.5" fill="${INK}">${esc(t.length > 24 ? t.replace('Worth if ', 'If ') : t)}</text>`;
    svg += `<rect x="${L}" y="${y + 3}" width="${w.toFixed(1)}" height="18" rx="3" fill="${c}"/>`;
    svg += `<text x="${(L + w + 6).toFixed(1)}" y="${y + 16}" font-size="10.5" font-weight="700" fill="${INK}">${esc(shortMoney(v, currency))}</text>`;
  });
  return svg + '</svg>';
}

/** Where the money sits now: one bar, with a key */
export function sitsBar(_ref4) {
  let {
    sits
  } = _ref4;
  const W = 340,
    H = 30;
  const keys = Object.keys(sits).filter(k => sits[k] > 0);
  let x = 0;
  let svg = `<svg class="VMoney-sits" viewBox="0 0 ${W} ${H + keys.length * 0 + 2}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(keys.map(k => HOME_LABELS[k] + ' ' + Math.round(sits[k] * 100) + '%').join(', '))}">`;
  keys.forEach(k => {
    const w = W * sits[k];
    svg += `<rect x="${x.toFixed(1)}" y="2" width="${Math.max(1, w - 2).toFixed(1)}" height="26" rx="3" fill="${HOME_COLOURS[k]}"/>`;
    if (w > 36) svg += `<text x="${(x + w / 2).toFixed(1)}" y="19" text-anchor="middle" font-size="11" font-weight="700" fill="#fff">${Math.round(sits[k] * 100)}%</text>`;
    x += w;
  });
  return svg + '</svg>';
}
export const sitsColour = k => HOME_COLOURS[k];
flarum.reg.add('voucha-assessments', 'common/money', { HOME_LABELS: HOME_LABELS,timeline: timeline,stressBars: stressBars,sitsBar: sitsBar,sitsColour: sitsColour, });